import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { Chapter, Reveal } from "./shared";
import { api } from "@/lib/api";
import { avatarFor } from "@/lib/avatar";

const GOOGLE_URL = "https://share.google/X47z6DTDFc8Cvm166";

const FALLBACK = [
  { id: "f1", name: "Andreia Barbosa", text: "Rapidez e qualidade no serviço!", rating: 5, source: "google" },
  { id: "f2", name: "Kassia Almeida", text: "Ótimo atendimento, o prestador do serviço muito educado e eficiente! O trabalho ficou perfeito.", rating: 5, source: "google" },
  { id: "f3", name: "Ivanilda Figueiredo", text: "Empresa muito boa, muita dedicação e agilidade.", rating: 5, source: "google" },
];

export const Testimonials = () => {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => setReviews(r.data.length ? r.data : FALLBACK))
      .catch(() => setReviews(FALLBACK));
  }, []);

  const list = reviews || FALLBACK;

  return (
    <section id="depoimentos" className="bg-zinc-50 py-24 sm:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <Chapter num="06 — Prova social" title="O que" accent="nossos clientes dizem" />
        <Reveal className="-mt-6 mb-10 text-center">
          <div className="inline-flex items-center gap-3 rounded-full border border-zinc-200 bg-white px-5 py-2.5 shadow-sm" data-testid="google-rating-pill">
            <span className="font-display text-xl font-bold text-[#4285F4]">G</span>
            <span className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={15} className="fill-[var(--primary)] text-[var(--primary)]" />
              ))}
            </span>
            <span className="text-sm font-semibold text-zinc-800">5.0 no Google</span>
            <span className="text-xs text-zinc-500">· 8 avaliações reais</span>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {list.slice(0, 6).map((r, i) => (
            <Reveal key={r.id} delay={(i % 3) * 0.12}>
              <div
                data-testid={`review-card-${i}`}
                className="h-full rounded-2xl border border-zinc-200 bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={r.avatar || avatarFor(r.name)}
                    alt={`Foto de ${r.name}`}
                    loading="lazy"
                    className="h-11 w-11 rounded-full border border-zinc-200 object-cover"
                    data-testid={`review-avatar-${i}`}
                  />
                  <div>
                    <p className="font-semibold text-zinc-900">{r.name}</p>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star
                          key={j}
                          size={13}
                          className={j < r.rating ? "fill-[var(--primary)] text-[var(--primary)]" : "fill-zinc-200 text-zinc-200"}
                        />
                      ))}
                    </div>
                  </div>
                  {r.source === "google" && (
                    <span className="ml-auto font-display text-xl font-bold text-[#4285F4]" title="Avaliação do Google">
                      G
                    </span>
                  )}
                </div>
                <p className="mt-4 text-sm italic leading-relaxed text-zinc-600">“{r.text}”</p>
                {r.created_at && (
                  <p className="mt-3 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
                    Publicado em Google · {new Date(r.created_at).toLocaleDateString("pt-BR")}
                  </p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-10 text-center">
          <a
            href={GOOGLE_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="reviews-google-link"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-zinc-700 transition-colors hover:border-[var(--primary)] hover:text-[#E8A200]"
          >
            Ver todas as avaliações no Google
            <ExternalLink size={15} />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
